import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Compass, LogOut, Menu, X, GraduationCap, Wrench } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PATHFINDER_NAME } from "@/data/v2Config";

const NAV = [
  { label: "Learn", to: "/v2", icon: GraduationCap, match: (p) => p === "/v2" || p.startsWith("/v2/module") || p.startsWith("/v2/learn") },
  { label: "Pro Tools", to: "/v2/pro", icon: Wrench, match: (p) => p.startsWith("/v2/pro") },
];

export default function SiteHeaderV2() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => { setOpen(false); }, [pathname]);

  const doLogout = async () => {
    setOpen(false);
    await logout();
    navigate("/v2");
  };

  const renderLink = (n, prefix, onClick) => {
    const active = n.match(pathname);
    const Icon = n.icon;
    return (
      <Link
        key={n.to}
        to={n.to}
        onClick={onClick}
        data-testid={`${prefix}${n.label.toLowerCase().replace(/\s/g, "-")}`}
        className={`text-xs tracking-[0.15em] uppercase font-bold transition-colors inline-flex items-center gap-1.5 whitespace-nowrap ${
          active ? "text-brand" : "text-slate-400 hover:text-slate-900"
        }`}
      >
        <Icon className="w-3.5 h-3.5" /> {n.label}
      </Link>
    );
  };

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-200">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between gap-4">
        <Link to="/v2" data-testid="v2-brand-home" className="flex items-center gap-2 font-heading font-black text-lg tracking-tight text-slate-900 shrink-0">
          <Compass className="w-6 h-6 text-brand" strokeWidth={2.5} />
          <span>RouteHaul<span className="hidden sm:inline text-slate-400 font-bold"> Pathfinder</span></span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-6 justify-end">
          {NAV.map((n) => renderLink(n, "v2nav-"))}
          {user ? (
            <button onClick={doLogout} data-testid="v2-logout" className="text-xs tracking-[0.15em] uppercase font-bold text-slate-400 hover:text-slate-900 inline-flex items-center gap-1 whitespace-nowrap">
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          ) : (
            <Link to="/login" data-testid="v2-nav-login" className="text-xs tracking-[0.15em] uppercase font-bold text-white bg-brand hover:bg-brand-hover rounded-full px-4 py-2 transition-colors whitespace-nowrap">
              Log in
            </Link>
          )}
        </nav>

        {/* Mobile toggle */}
        <button
          onClick={() => setOpen((v) => !v)}
          data-testid="v2-mobile-toggle"
          aria-label="Toggle menu"
          className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-md text-slate-700 hover:bg-slate-100 transition-colors shrink-0"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <nav data-testid="v2-mobile-menu" className="lg:hidden border-t border-slate-200 bg-white px-6 py-5 flex flex-col gap-5">
          {NAV.map((n) => renderLink(n, "v2mnav-", () => setOpen(false)))}
          {user ? (
            <button onClick={doLogout} data-testid="v2-mobile-logout" className="text-xs tracking-[0.15em] uppercase font-bold text-slate-400 hover:text-slate-900 inline-flex items-center gap-1 self-start">
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          ) : (
            <Link to="/login" onClick={() => setOpen(false)} data-testid="v2mnav-login" className="text-xs tracking-[0.15em] uppercase font-bold text-white bg-brand hover:bg-brand-hover rounded-full px-4 py-2 transition-colors self-start">
              Log in
            </Link>
          )}
        </nav>
      )}
    </header>
  );
}
